/**
 * Logo thật của các nền tảng/ứng dụng — tự host trong `public/brand-logos/`
 * để không phụ thuộc CDN lúc chạy. Nguồn gốc + giấy phép từng file ghi ở
 * `public/brand-logos/CREDITS.md`.
 *
 * Simple Icons (CC0) chỉ có hình đơn sắc → đã tô sẵn màu thương hiệu (`hex`
 * trong dữ liệu Simple Icons) vào file SVG. Thương hiệu Simple Icons không có
 * (Slack, WeCom, Pancake, KiotViet, Sapo, Haravan) lấy từ trang chính
 * thức của hãng hoặc Wikimedia Commons.
 *
 * Logo là nhãn hiệu của chủ sở hữu, chỉ dùng để nhận diện kênh kết nối.
 */
const SI_VERSION = '16.33.0'
const si = (slug: string) => `https://cdn.jsdelivr.net/npm/simple-icons@${SI_VERSION}/icons/${slug}.svg`

export interface BrandLogoDef {
  /** Đường dẫn file đã tự host. */
  src: string
  /** Màu thương hiệu chính (hex). */
  color: string
  /** Nơi lấy logo gốc. */
  sourceUrl: string
}

export const BRAND_LOGOS = {
  zalo: { src: '/brand-logos/zalo.svg', color: '#0068FF', sourceUrl: si('zalo') },
  // Dấu Zalo OA tự vẽ (tròn xanh Zalo + chữ "OA") — phân biệt với Zalo cá nhân trên chấm góc ảnh.
  zaloOa: { src: '/brand-logos/zalo-oa.svg', color: '#0068FF', sourceUrl: 'Tự vẽ trong repo (public/brand-logos/zalo-oa.svg)' },
  facebook: { src: '/brand-logos/facebook.svg', color: '#0866FF', sourceUrl: si('facebook') },
  messenger: { src: '/brand-logos/messenger.svg', color: '#0866FF', sourceUrl: si('messenger') },
  instagram: { src: '/brand-logos/instagram.svg', color: '#FF0069', sourceUrl: si('instagram') },
  tiktok: { src: '/brand-logos/tiktok.svg', color: '#000000', sourceUrl: si('tiktok') },
  shopee: { src: '/brand-logos/shopee.svg', color: '#EE4D2D', sourceUrl: si('shopee') },
  telegram: { src: '/brand-logos/telegram.svg', color: '#26A5E4', sourceUrl: si('telegram') },
  whatsapp: { src: '/brand-logos/whatsapp.svg', color: '#25D366', sourceUrl: si('whatsapp') },
  line: { src: '/brand-logos/line.svg', color: '#00C300', sourceUrl: si('line') },
  youtube: { src: '/brand-logos/youtube.svg', color: '#FF0000', sourceUrl: si('youtube') },
  x: { src: '/brand-logos/x.svg', color: '#000000', sourceUrl: si('x') },
  wechat: { src: '/brand-logos/wechat.svg', color: '#07C160', sourceUrl: si('wechat') },
  vk: { src: '/brand-logos/vk.svg', color: '#0077FF', sourceUrl: si('vk') },
  shopify: { src: '/brand-logos/shopify.svg', color: '#7AB55C', sourceUrl: si('shopify') },
  googlesheets: { src: '/brand-logos/googlesheets.svg', color: '#34A853', sourceUrl: si('googlesheets') },
  slack: {
    src: '/brand-logos/slack.svg',
    color: '#4A154B',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Slack_icon_2019.svg',
  },
  wecom: {
    src: '/brand-logos/wecom.png',
    color: '#2B7FF6',
    sourceUrl: 'https://work.weixin.qq.com/',
  },
  pancake: { src: '/brand-logos/pancake.svg', color: '#1260E4', sourceUrl: 'https://pancake.vn/static/images/new_fa_icon.svg' },
  kiotviet: {
    src: '/brand-logos/kiotviet.svg',
    color: '#0070F4',
    sourceUrl: 'https://cdn-kvweb-website.kiotviet.vn/images/v2/giai-phap/logos/kiotviet-logo-horizontal.svg',
  },
  sapo: { src: '/brand-logos/sapo.png', color: '#1BC47D', sourceUrl: 'https://www.sapo.vn/favicon.ico' },
  haravan: { src: '/brand-logos/haravan.svg', color: '#1A67A3', sourceUrl: 'https://www.haravan.com/' },
  evotech: { src: '/apps/evotech.svg', color: '#0045FF', sourceUrl: 'evotech-logo.png (logo nội bộ trong repo)' },
  // ── Nền tảng AI (nhân viên AI) ──
  openai: {
    src: '/brand-logos/openai.svg',
    color: '#000000',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:OpenAI_logo_2025_(symbol).svg',
  },
  claude: { src: '/brand-logos/claude.svg', color: '#D97757', sourceUrl: si('claude') },
  gemini: { src: '/brand-logos/gemini.svg', color: '#8E75B2', sourceUrl: si('googlegemini') },
  grok: { src: '/brand-logos/grok.svg', color: '#000000', sourceUrl: 'https://commons.wikimedia.org/wiki/File:Grok-icon.svg' },
  deepseek: { src: '/brand-logos/deepseek.svg', color: '#5786FE', sourceUrl: si('deepseek') },
} satisfies Record<string, BrandLogoDef>

export type BrandKey = keyof typeof BRAND_LOGOS
